import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  signal,
  ViewChild,
  effect,
  DestroyRef, ElementRef,
} from '@angular/core';
import { GoogleMap, MapInfoWindow } from '@angular/google-maps';
import { Cluster, MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { BehaviorSubject, Observable, of, startWith, switchMap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { UiPreloaderComponent } from '../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { PreloaderColor } from '@enums/shared.enum';
import { Store } from '@ngrx/store';
import { MapSelectors } from '@states/map/map.selector-types';
import { MapActions } from '@states/map/map.action-types';
import { PipesModule } from '../../pipes/pipes.module';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { TranslatePipe } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';

interface MarkerInfo {
  a: string;
  b: string;
  c: string;
  d: string;
  e: number;
  f: number;
}

const defaultCoords = { lat: 37, lng: -100 };
const defaultZoom = 5;

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrl: './map.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    GoogleMap,
    MapInfoWindow,
    AsyncPipe,
    UiPreloaderComponent,
    PipesModule,
    LazyLoadImageModule,
    ReactiveFormsModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    TranslatePipe,
    MatTooltip,
  ],
})
export class MapComponent implements OnInit, AfterViewInit {
  @ViewChild('googleMap', { static: false }) googleMap: GoogleMap;
  @ViewChild(MapInfoWindow) infoWindow: MapInfoWindow;
  public selectedMarker$: BehaviorSubject<MarkerInfo> = new BehaviorSubject<MarkerInfo>(null);
  public isLoading = signal<boolean>(true);
  public defaultCoords = signal(defaultCoords);

  private markers: google.maps.Marker[] = [];
  private markerCluster: MarkerClusterer | null = null;

  public nameControl = new FormControl('');
  private selectedObject = signal(null);
  private isSelectedObjectInfo = false;

  private allObjects: MarkerInfo[] = [];
  private mapListenersAttached = false;
  private rebuildTimeoutId: number | null = null;
  public filteredObjects$: Observable<MarkerInfo[]>;

  private isZooming = false;

  constructor(private cdr: ChangeDetectorRef, private store$: Store, private destroyRef: DestroyRef) {
    effect(() => {
      const map = this.googleMap?.googleMap;
      const objects = this.selectObjects();

      this.filteredObjects$ = this.nameControl.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((value: string | null) => {
          const filterValue = typeof value === 'string' ? value.toLowerCase() : null;
          if (!filterValue) {
            return of([]);
          }
          const filtered = this.allObjects
            .filter((o) => o.b.toLowerCase().includes(filterValue))
            .slice(0, 100);
          if (filtered.length === 1) {
            this.selectedObject.set(filtered[0]);
          }
          return of(filtered);
        }),
      );

      if (!map || !objects || objects.length === 0) {
        return;
      }

      this.allObjects = objects;

      if (!this.mapListenersAttached) {
        this.attachMapListeners(map);
        this.mapListenersAttached = true;

        this.rebuildMarkersForCurrentView(map);
      }
      map.setOptions({
        minZoom: 5,
        maxZoom: 20,
        mapTypeControl: true,
      });
    });
  }

  public selectObjects = this.store$.selectSignal(MapSelectors.selectAllMapObjects);
  public selectMarkerPicture = this.store$.selectSignal(MapSelectors.selectImageUrl);
  public selectLoading = this.store$.selectSignal(MapSelectors.selectIsLoading);

  ngOnInit() {
    this.store$.dispatch(MapActions.getAllObjects());
  }

  public onObjectBlur() {
    const control = this.nameControl;
    const inputValue = control.value?.trim();
    if (!inputValue) return;

    const matched = this.selectObjects().find((o) => o.b.toLowerCase() === inputValue.toLowerCase());

    if (matched) {
      control.setValue(matched.b, { emitEvent: false });
    }
  }

  public displayObjectName(objectName: string): string {
    const object = this.selectObjects().find((o) => o.b === objectName);
    return object ? object.b : '';
  }

  public onObjectSelected(selectedName: string) {
    this.nameControl.setValue(selectedName, { emitEvent: false });
    this.navigate();
  }

  ngAfterViewInit() {
  }

  private attachMapListeners(map: google.maps.Map) {
    map.addListener('zoom_changed', () => {
      if (this.isZooming) return;
      this.isZooming = true;
      this.clearMarkersAndCluster();
    });

    map.addListener('idle', () => {
      if (this.rebuildTimeoutId !== null) {
        clearTimeout(this.rebuildTimeoutId);
      }

      this.rebuildTimeoutId = window.setTimeout(() => {
        this.rebuildMarkersForCurrentView(map);
        this.isZooming = false;
      }, 120);
    });
  }

  public navigate(): void {
    if (this.selectedObject()) {
      this.closeInfo();
      this.isSelectedObjectInfo = true;
      this.defaultCoords.set({ lat: this.selectedObject().e, lng: this.selectedObject().f });
      this.googleMap.googleMap.setZoom(20);
    }
  }

  private clearMarkersAndCluster() {
    if (this.markerCluster) {
      this.markerCluster.clearMarkers();
      this.markerCluster.setMap(null);
      this.markerCluster = null;
    }

    this.markers.forEach((m) => m.setMap(null));
    this.markers = [];
  }

  private rebuildMarkersForCurrentView(map: google.maps.Map) {
    const bounds = map.getBounds();
    if (!bounds || !this.allObjects || this.allObjects.length === 0) return;

    const visibleObjects = this.allObjects.filter((obj) =>
      bounds.contains(new google.maps.LatLng(obj.e, obj.f)),
    );

    const newIds = new Set(visibleObjects.map((o) => o.a));
    const oldIds = new Set(this.markers.map((m) => (m as any)._objId));

    this.markers = this.markers.filter((marker) => {
      const id = (marker as any)._objId;
      if (!newIds.has(id)) {
        marker.setMap(null);
        return false;
      }
      return true;
    });

    for (const obj of visibleObjects) {
      if (!oldIds.has(obj.a)) {
        const m = new google.maps.Marker({
          position: { lat: obj.e, lng: obj.f },
          icon: 'assets/icons/camera-marker.svg',
        });

        m.addListener('click', () => {
          this.onMarkerClick(obj, m);
        });

        (m as any)._objId = obj.a;
        this.markers.push(m);
      }
    }

    if (this.markerCluster) {
      this.markerCluster.clearMarkers();
      this.markerCluster.setMap(null);
    }

    this.markerCluster = new MarkerClusterer({
      map,
      markers: this.markers,
      renderer: this.customClusterRenderer(),
      algorithm: new SuperClusterAlgorithm({
        radius: 150,
        maxZoom: 18,
      }),
    });

    this.isLoading.set(false);
    if (this.selectedObject() && this.isSelectedObjectInfo) {
      this.onNavigateMarker();
    }
  }

  private customClusterRenderer() {
    return {
      render: ({ count, position }: Cluster) => {
        const el = document.createElement('div');
        el.className = 'cluster-marker';

        const size = Math.min(20 + count * 0.3, 40);
        const fontSize = Math.min(10 + count * 0.05, 14);

        el.style.width = `${size}px`;
        el.style.height = `${size}px`;
        el.style.borderRadius = '50%';
        el.style.backgroundColor = '#3456E0';
        el.style.display = 'flex';
        el.style.justifyContent = 'center';
        el.style.alignItems = 'center';
        el.style.position = 'relative';

        const number = document.createElement('span');
        number.className = 'cluster-number';
        number.innerText = count.toString();
        number.style.fontSize = `${fontSize}px`;
        number.style.color = 'white';
        number.style.fontWeight = 'bold';

        el.appendChild(number);

        return new google.maps.marker.AdvancedMarkerElement({
          position,
          content: el,
        });
      },
    };
  }

  private onMarkerClick(markerObject: MarkerInfo, marker: google.maps.Marker) {
    this.store$.dispatch(MapActions.resetImageUrl());
    this.store$.dispatch(MapActions.getMarkerPicture({ id: markerObject.a }));
    this.selectedMarker$.next(markerObject);
    this.infoWindow.infoWindow.open({
      anchor: marker,
    });
  }

  private onNavigateMarker() {
    let selectedMarker;
    if (this.selectedObject()) {
      selectedMarker = this.markers.find(
        (m) => String((m as any)._objId) === String(this.selectedObject().a),
      );
    }
    if (!this.selectedObject() && !selectedMarker) {
      return;
    }
    this.store$.dispatch(MapActions.resetImageUrl());
    this.store$.dispatch(MapActions.getMarkerPicture({ id: this.selectedObject().a }));
    this.selectedMarker$.next(this.selectedObject());
    this.isSelectedObjectInfo = false;
    this.infoWindow.infoWindow.open({
      map: this.googleMap.googleMap,
      anchor: selectedMarker,
    });
  }

  public closeInfo(): void {
    this.store$.dispatch(MapActions.resetImageUrl());
    this.selectedMarker$.next(null);
    this.infoWindow.close();
  }

  protected readonly loaderColor = PreloaderColor;

  public clean() {
    this.nameControl.reset();
    this.selectedObject.set(null);
    this.closeInfo();
    this.isSelectedObjectInfo = false;
    this.defaultCoords.set(defaultCoords);
    this.googleMap.googleMap.setZoom(defaultZoom);
  }
}
